'use client'

import { faFacebook, faGoogle, faApple } from '@fortawesome/free-brands-svg-icons'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'

export interface EnabledProviders {
  google: boolean
  facebook: boolean
  apple: boolean
}

type SocialProvider = 'google' | 'facebook' | 'apple'

interface SocialLoginButtonsProps {
  onFacebookClick?: () => void
  onGoogleClick?: () => void
  onAppleClick?: () => void
  mode: 'login' | 'register'
  enabledProviders?: EnabledProviders
  disabled?: boolean
  loadingProvider?: SocialProvider | null
}

function constructFaSvg(icon: IconDefinition): string {
  if (!icon || !icon.icon || !icon.icon[0] || !icon.icon[1] || !icon.icon[4]) {
    return ''
  }

  const viewBox = `${icon.icon[0]} ${icon.icon[1]}`
  const path = icon.icon[4]

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${viewBox}"><path fill="currentColor" d="${path}"/></svg>`
}

export default function SocialLoginButtons({
  onFacebookClick,
  onGoogleClick,
  onAppleClick,
  mode,
  enabledProviders = { google: true, facebook: true, apple: true },
  disabled = false,
  loadingProvider = null,
}: SocialLoginButtonsProps) {
  const prefix = mode === 'login' ? 'Belépés a következővel:' : 'Regisztrálás a következővel:'
  const loadingText = 'Betöltés...'
  const disabledClass = 'disabled:cursor-not-allowed disabled:opacity-60'

  // Check if any providers are enabled
  const hasAnyProvider =
    enabledProviders.facebook || enabledProviders.google || enabledProviders.apple

  // Don't render anything if no providers are enabled
  if (!hasAnyProvider) {
    return null
  }

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Facebook Button */}
      {enabledProviders.facebook && (
        <button
          type="button"
          onClick={onFacebookClick}
          disabled={disabled || loadingProvider !== null}
          className={`flex w-full cursor-pointer items-center justify-center gap-3 rounded-md border border-[#1A76F2] bg-[#1A76F2] px-3 py-2.5 text-white transition-opacity hover:opacity-90 md:gap-4 md:px-5 ${disabledClass}`}
        >
          <span
            className="-mt-px inline-flex h-5 w-5 shrink-0"
            dangerouslySetInnerHTML={{ __html: constructFaSvg(faFacebook) }}
          />
          <span className="text-body">
            {loadingProvider === 'facebook' ? (
              loadingText
            ) : (
              <>
                {prefix} <span className="font-bold">Facebook</span>
              </>
            )}
          </span>
        </button>
      )}

      {/* Google Button */}
      {enabledProviders.google && (
        <button
          type="button"
          onClick={onGoogleClick}
          disabled={disabled || loadingProvider !== null}
          className={`flex w-full cursor-pointer items-center justify-center gap-3 rounded-md border border-white bg-[#F1F1F1] px-3 py-2.5 text-[#110607] transition-opacity hover:opacity-90 md:gap-4 md:px-5 ${disabledClass}`}
        >
          <span
            className="-mt-px inline-flex h-5 w-5 shrink-0 text-[#EA4435]"
            dangerouslySetInnerHTML={{ __html: constructFaSvg(faGoogle) }}
          />
          <span className="text-body">
            {loadingProvider === 'google' ? (
              loadingText
            ) : (
              <>
                {prefix} <span className="font-bold">Google</span>
              </>
            )}
          </span>
        </button>
      )}

      {/* Apple Button */}
      {enabledProviders.apple && (
        <button
          type="button"
          onClick={onAppleClick}
          disabled={disabled || loadingProvider !== null}
          className={`flex w-full cursor-pointer items-center justify-center gap-3 rounded-md border border-[#110607] bg-[#110607] px-3 py-2.5 text-white transition-opacity hover:opacity-90 md:gap-4 md:px-5 ${disabledClass}`}
        >
          <span
            className="-mt-px inline-flex h-5 w-5 shrink-0"
            dangerouslySetInnerHTML={{ __html: constructFaSvg(faApple) }}
          />
          <span className="text-body">
            {loadingProvider === 'apple' ? (
              loadingText
            ) : (
              <>
                {prefix} <span className="font-bold">Apple</span>
              </>
            )}
          </span>
        </button>
      )}
    </div>
  )
}
